import React, { useState } from 'react';
import { Upload, Check, Trash2 } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

export default function FaviconUpload({ favicon, onUpload, onDelete, isUploading }) {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(favicon?.url || null);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFile(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleChange = (e) => {
    e?.preventDefault();
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFile(e?.target?.files?.[0]);
    }
  };

  const handleFile = (file) => {
    // Validate file type
    const allowedTypes = ['image/svg+xml', 'image/x-icon', 'image/png'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Invalid file type. Please upload an SVG, ICO, or PNG file.');
      return;
    }

    // Validate file size (512KB max)
    if (file?.size > 524288) {
      alert('File size must be less than 512KB.');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e?.target?.result);
    };
    reader?.readAsDataURL(file);

    // Call upload handler
    onUpload(file);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete the site favicon? This will remove it from the browser tab.')) {
      setPreviewUrl(null);
      onDelete();
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Favicon Display */}
      {(previewUrl || favicon?.url) && (
        <div className="bg-white bg-opacity-10 rounded-xl p-6 border border-white border-opacity-10">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold text-white mb-1">Current Favicon</h4>
              <p className="text-sm text-blue-100">
                This favicon will appear in browser tabs
              </p>
            </div>
            <Button
              onClick={handleDelete}
              disabled={isUploading}
              variant="outline"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              className="border-red-400 text-red-300 hover:bg-red-500 hover:bg-opacity-20 rounded-xl disabled:opacity-50"
            >
              Remove
            </Button>
          </div>

          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-xl flex items-center justify-center p-2 border-2 border-white border-opacity-20">
              <img
                src={previewUrl || favicon?.url}
                alt="Site favicon preview"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            <div className="flex-1">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-blue-200">File Name</p>
                  <p className="font-semibold text-white truncate">{favicon?.fileName || 'favicon'}</p>
                </div>
                <div>
                  <p className="text-blue-200">File Type</p>
                  <p className="font-semibold text-white">{favicon?.fileType || 'image/svg+xml'}</p>
                </div>
                <div>
                  <p className="text-blue-200">File Size</p>
                  <p className="font-semibold text-white">
                    {favicon?.fileSize ? `${(favicon?.fileSize / 1024)?.toFixed(2)} KB` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-blue-200">Uploaded</p>
                  <p className="font-semibold text-white">
                    {favicon?.uploadedAt ? new Date(favicon.uploadedAt)?.toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Upload Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-8 transition-all ${
          dragActive
            ? 'border-blue-400 bg-blue-500 bg-opacity-10' :'border-white border-opacity-20 bg-white bg-opacity-5'
        }`}
      >
        <input
          type="file"
          id="favicon-upload"
          accept=".svg,.ico,.png,image/svg+xml,image/x-icon,image/png"
          onChange={handleChange}
          className="hidden"
          disabled={isUploading}
        />

        <label
          htmlFor="favicon-upload"
          className="flex flex-col items-center justify-center cursor-pointer"
        >
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
            dragActive ? 'bg-blue-500 bg-opacity-20' : 'bg-white bg-opacity-10'
          }`}>
            {isUploading ? (
              <div className="animate-spin">
                <Icon name="Loader" size={32} className="text-blue-300" />
              </div>
            ) : (
              <Icon name="Upload" size={32} className="text-blue-300" />
            )}
          </div>

          <p className="text-lg font-semibold text-white mb-2">
            {isUploading ? 'Uploading...' : dragActive ? 'Drop favicon here' : 'Upload Site Favicon'}
          </p>

          <p className="text-sm text-blue-100 text-center mb-4">
            Drag and drop your favicon file here, or click to browse
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-blue-200">
            <div className="flex items-center space-x-1">
              <Icon name="Check" size={14} className="text-green-400" />
              <span>SVG, ICO, PNG</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Check" size={14} className="text-green-400" />
              <span>Max 512KB</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Check" size={14} className="text-green-400" />
              <span>Recommended: 32x32 or 64x64</span>
            </div>
          </div>
        </label>
      </div>
      {/* Info Box */}
      <div className="bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-200 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-100 space-y-2">
            <p className="font-semibold text-white">Favicon Guidelines:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>SVG format is recommended for best quality and scalability</li>
              <li>Keep the design simple and recognizable at small sizes</li>
              <li>Use high contrast colors for better visibility</li>
              <li>After uploading, clear your browser cache to see changes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}